import { CasinoPage, MenuItem, MenuItemType, Settings } from "../../http/protocol";

import { t } from "../../i18n";
import { getContainer } from "../../ioc/IOCSetup";
import { toSEOFriendlyString } from "../../user/ui/utils";

export interface Icon {
     className?: string;
     text?: string;
}
export interface MenuItemData {
     type: MenuItemType;
     name?: string;
     active: boolean;
     icon?: Icon;
     iconText?: string;
     text: string;
     url: string;
     external?: boolean;
     target?: string;
     className?: string;
     subMenu?: SubMenu;
     expandSubmenuOnly?: boolean;
     supportLink?: boolean;
     disableSidebarCollapse?: boolean;
}

export interface SubMenu {
     defaultCasinoPage?: boolean;
     icon?: string;
     id?: number;
     name?: string;
     tabs?: SubMenuTab[];
}

export interface SubMenuTab {
     icon?: string;
     id?: number;
     name?: string;
     target?: string;
     translations?: { [index: string]: string };
     type?: MenuItemType;
     url?: string;
     className: string;
}

export const getMenuUrl = (m: MenuItem): string => {
     if (m.type == MenuItemType.CUSTOM) {
          if (m.url) {
               return m.url;
          } else {
               return "/";
          }
     } else {
          let path = "";
          if (m.type == MenuItemType.CASINO && m.subMenu == undefined) {
               path = "casino";
          } else if (m.type == MenuItemType.CASINO && m.subMenu !== undefined) {
               let casinoUrl = isDefaultPage(m);
               return casinoUrl ?? "/";
          } else if (m.type == MenuItemType.POKER) {
               path = "poker";
          } else if (m.type == MenuItemType.RACING) {
               path = "racing";
          } else if (m.type == MenuItemType.SPORTSBOOK) {
               path = "sportsbook";
          }
          return "/" + path;
     }
};

export const getActiveClass = (path: string, m?: MenuItem, casioPage?: CasinoPage): boolean => {
     if (!m) {
          return false;
     }

     if (m.type == MenuItemType.CUSTOM && m.url && m.url.startsWith("/") && isExactMatch(path, m.url)) {
          return true;
     } else if (m.type == MenuItemType.POKER && isActiveSection("/poker", path)) {
          return true;
     } else if (m.type == MenuItemType.SPORTSBOOK && isActiveSection("/sportsbook", path)) {
          return true;
     } else if (m.type == MenuItemType.RACING && isActiveSection("/racing", path)) {
          return true;
     } else if (m.type == MenuItemType.CASINO && (casioPage?.defaultPage ?? true) && isActiveSection("/casino", path)) {
          return true;
     } else if (m.type == MenuItemType.CUSTOM && m.url && casioPage && casioPage.defaultPage == false && m.url.startsWith("/casino/page/" + casioPage.id) && path.startsWith("/casino/page/" + casioPage.id)) {
          return true;
     }
     return false;
};
export const getTranslationMap = (lng: string, defaultTranslation?: string, translations?: { [key: string]: string }) => {
     if (translations && translations[lng]) {
          return translations[lng];
     } else {
          return defaultTranslation;
     }
};
export const getItemText = (lng: string, menuItem: MenuItem): string => {
     if (menuItem.type == MenuItemType.CUSTOM) {
          let translation = getTranslationMap(lng, menuItem.name, menuItem.translations);
          if (!translation) {
               return "";
          }
          return translation;
     } else if (menuItem.type == MenuItemType.CASINO && menuItem.name) {
          let translation = getTranslationMap(lng, menuItem.name, menuItem.translations);
          if (!translation) {
               return "";
          }
          return translation;
     }
     return t("navmenu." + menuItem.type);
};
export const getItemIcon = (menuItem: MenuItem): Icon => {
     let icon = "";
     let text = undefined;
     switch (menuItem.type) {
          case MenuItemType.CASINO:
               icon = "custom-icons-cherry";
               break;
          case MenuItemType.SPORTSBOOK:
               icon = "material-icons";
               text = "sports_baseball";
               break;
          case MenuItemType.POKER:
               icon = "custom-icons-spade";
               break;
          case MenuItemType.RACING:
               icon = "" + "custom-icons-horse";
               break;
          case MenuItemType.CUSTOM:
               icon = "custom-icons custom-icons-" + toSEOFriendlyString(menuItem.name);
               break;
          default:
               icon = "";
               break;
     }
     return { className: icon, text: text };
};
export const isProductEnabled = (type?: MenuItemType, settings?: Settings) => {
     if (type == MenuItemType.CUSTOM) {
          return true;
     }
     if (!type || !settings) {
          return false;
     }
     if (type == MenuItemType.POKER) {
          return settings.pokerEnabled;
     } else if (type == MenuItemType.SPORTSBOOK) {
          return settings.sportsbookEnabled;
     } else if (type == MenuItemType.CASINO) {
          return settings.casinoEnabled;
     } else if (type == MenuItemType.RACING) {
          return settings.racingEnabled;
     }

     return false;
};

export function isExactMatch(currentPath: string, itemPath?: string): boolean {
     return typeof itemPath != "undefined" && itemPath == currentPath;
}
export function isActiveSection(sectionPath: string, currentPath: string) {
     let menu = getContainer().getSettingsService().getMenu();
     if (menu.items && menu.items.find((i) => i.type == MenuItemType.CUSTOM && i.url === currentPath)) {
          return false;
     }
     return currentPath.startsWith(sectionPath);
}

export const isExternalLink = (m: MenuItem) => {
     return !m.url?.startsWith("/") && m.type == MenuItemType.CUSTOM;
};
export const pathToClass = (m: MenuItem) => {
     if (isExternalLink(m)) {
          return "external";
     } else {
          let menuUrl = getMenuUrl(m);
          if (menuUrl.startsWith("/")) {
               menuUrl = menuUrl.replace("/", "");
          }
          return menuUrl.replaceAll("/", "-");
     }
};
export const mapMenuItemData = (m: MenuItem, lng: string, pathname: string, casinoPage?: CasinoPage) => {
     let mapped: MenuItemData = {
          type: m.type ? m.type : MenuItemType.CUSTOM,
          name: m.name ? m.name : m.type,
          text: getItemText(lng, m),
          active: getActiveClass(pathname, m, casinoPage),
          icon: m.icon ? { className: m.icon } : getItemIcon(m),
          url: getMenuUrl(m),
          external: isExternalLink(m),
          className: m.type + " " + toSEOFriendlyString(m.name),
          subMenu:
               m.subMenu !== undefined
                    ? {
                           defaultCasinoPage: m.subMenu.defaultCasinoPage,
                      }
                    : undefined,
          target: m.target,
     };
     return mapped;
};

export function isCasinoMenuItemActive(match: any, location: any, m: MenuItemData) {
     if (m.type === MenuItemType.CASINO || m.type === MenuItemType.CUSTOM) {
          const ma = location.pathname.match(/\/page\/(\d+)\//);
          const current = m.url.match(/\/page\/(\d+)\//);
          const cpageId = current ? current[1] : null;
          const pageId = ma ? ma[1] : null;
          if (cpageId == null && pageId == null) {
               if (m.subMenu?.defaultCasinoPage) {
                    return m.active;
               } else {
                    return m.active;
               }
          } else if (cpageId === pageId) {
               return true;
          } else {
               return match?.isExact ?? false;
          }
     } else {
          return match?.isExact ?? false;
     }
}
/* ------------------------------------------------------------------------------------------------------------------ */
/* ------------------------------------------------------------------------------------------------------------------ */

export const mapMenuItem = (item: MenuItem, lng: string, pathname: string) => {
     /* if(item.supportLink) return; */
     if (item.includeInSidebarMenu) {
          switch (item.type) {
               case MenuItemType.CASINO:
                    if (item.subMenu) {
                         return mapCasinoMenu(item, lng, pathname);
                    } else {
                         return mapMenuItemDataSideMenu(item, lng, pathname);
                    }
               case MenuItemType.CUSTOM:
                    if (item.subMenu) {
                         return mapCustomMenu(item, lng, pathname);
                    } else {
                         return mapMenuItemDataSideMenu(item, lng, pathname);
                    }
               default:
                    return mapMenuItemDataSideMenu(item, lng, pathname);
          }
     }
};

export const mapMenuItemDataSideMenu = (m: MenuItem, lng: string, pathname: string, casinoPage?: CasinoPage) => {
     let mapped: MenuItemData = {
          type: m.type ? m.type : MenuItemType.CUSTOM,
          name: m.name ? m.name : m.type,
          text: getItemText(lng, m) as string,
          active: getActiveClass(pathname, m, casinoPage),
          icon: m.icon ? { className: m.icon } : {},
          url: getMenuUrl(m),
          external: isExternalLink(m),
          className: m.type + " " + toSEOFriendlyString(m.name),
          target: m.target,
          supportLink: m.supportLink,
          disableSidebarCollapse: m.disableSidebarCollapse
     };
     return mapped;
};

const mapCasinoMenu = (item: MenuItem, lng: string, pathname: string) => {
     let mapped: MenuItemData = {
          type: item.type ? item.type : MenuItemType.CASINO,
          name: item.name ? item.name : item.type,
          url: item.subMenu ? isDefaultPage(item) : getMenuUrl(item),
          expandSubmenuOnly: item.expandSubmenuOnly,
          className: item.type + " " + toSEOFriendlyString(item.name),
          external: item.subMenu ? false : isExternalCustomLink(item.url ?? ""),
          active: getActiveClass(pathname, item, { defaultPage: item.subMenu?.defaultCasinoPage }),
          text: getItemText(lng, item) as string,
          icon: { className: item.icon ?? "", text: MenuItemType.CASINO.toString() },
          subMenu: mapSubMenu(lng, item)
     };
     return mapped;
};

const isDefaultPage = (item: MenuItem) => {
     if (item.subMenu?.defaultCasinoPage) {
          return `/casino`.toLowerCase();
     } else {
          return `/casino/page/${item.subMenu?.id}/${toSEOFriendlyString(item.subMenu?.name)}`.toLowerCase();
     }
};

const getSubMenuUrl = (item: MenuItem, tab: SubMenuTab | any) => {
     if (tab.type === MenuItemType.CASINO) {
          if (item.subMenu?.defaultCasinoPage) {
               return `/casino/${tab.id}/${toSEOFriendlyString(tab.name)}`.toLowerCase();
          } else {
               return `/casino/page/${item.subMenu?.id}/${toSEOFriendlyString(item.subMenu?.name)}/${tab.id}/${toSEOFriendlyString(tab.name)}`.toLowerCase();
          }
     } else if (tab.type === MenuItemType.CUSTOM) {
          return tab.url;
     }
};

/* ------------------------------------------------------------------------------------------------------------------ */
/* ------------------------------------------------------------------------------------------------------------------ */

const mapCustomMenu = (item: MenuItem, lng: string, pathname: string) => {
     let mapped: MenuItemData = {
          type: item.type ? item.type : MenuItemType.CUSTOM,
          name: item.name ? item.name : item.type,
          url: getMenuUrl(item),
          expandSubmenuOnly: item.expandSubmenuOnly,
          className: item.type + " " + toSEOFriendlyString(item.name),
          external: item.subMenu ? false : isExternalCustomLink(item.url ?? ""),
          active: getActiveClass(pathname, item, { defaultPage: item.subMenu?.defaultCasinoPage }),
          text: getItemText(lng, item) as string,
          icon: { className: item.icon ?? "", text: MenuItemType.CUSTOM.toString() },
          subMenu: mapSubMenu(lng, item)
     };
     return mapped;
};

/* ------------------------------------------------------------------------------------------------------------------ */
/* ------------------------------------------------------------------------------------------------------------------ */

export const isExternalCustomLink = (url: string) => {
     return !url?.startsWith("/");
};

const mapSubMenu = (lng: string, item?: MenuItem) => {
     if (!item?.subMenu) {
          return undefined;
     }
     return {
          id: item.subMenu.id,
          name: item.subMenu.name,
          tabs: item.subMenu.tabs?.map((tab) => {
               return {
                    id: tab.id,
                    url: getSubMenuUrl(item, tab),
                    type: tab.type,
                    target: tab.target ?? "",
                    icon: tab.icon,
                    name: getTranslationMap(lng, tab.name, tab.translations) as string,
                    className: item.type + " " + toSEOFriendlyString(tab.name),
               };
          }),
     };
};
